import { Capacitor } from '@capacitor/core';

import { getGaMeasurementId } from '@/utils/envVars';

// This module integrates GA4 and gtag.js. It stays deliberately thin. One init function injects
// the tag with a cross-domain linker. Typed helpers cover the three funnel events. Every function
// is a no-op off the web platform (Capacitor native) and when no measurement id is set. Callers
// can fire events without a guard.

type GtagFn = (...args: unknown[]) => void;

// gtag.js attaches `dataLayer` and `gtag` to window at runtime. This code types them locally
// instead of through a global `interface Window` augmentation, which the lint config disallows.
// Access them only through gtagWindow(). This keeps the rest of the module strongly typed.
type GtagWindow = Window & { dataLayer?: unknown[]; gtag?: GtagFn };

function gtagWindow(): GtagWindow {
	return window as GtagWindow;
}

// List both ends of the marketing-to-app hop here. This makes gtag decorate outbound links. It
// also makes gtag accept the inbound `_gl` param. The app then reuses the marketing site's
// client_id instead of creating a new one. This turns two visits into one attributable session.
// The onthebuck.com marketing site stays listed until it is decommissioned (target 2027-09).
// app.onthebuck.com is not listed because it only redirects to app.elkmoney.com.
const CROSS_DOMAINS = ['elkmoney.com', 'app.elkmoney.com', 'onthebuck.com'];

let initialized = false;

function isWeb(): boolean {
	return Capacitor.getPlatform() === 'web';
}

/**
 * Inject gtag.js and configure the shared GA4 property with cross-domain linking.
 * Call this once at startup. Repeat calls and non-web platforms are no-ops.
 */
export function initAnalytics(): void {
	if (initialized || !isWeb()) {
		return;
	}

	const measurementId = getGaMeasurementId();
	if (!measurementId) {
		return;
	}

	const w = gtagWindow();
	const dataLayer = w.dataLayer ?? (w.dataLayer = []);
	w.gtag = (...args: unknown[]) => {
		dataLayer.push(args);
	};

	w.gtag('js', new Date());
	w.gtag('config', measurementId, {
		linker: { domains: CROSS_DOMAINS },
		// The SPA fires page_view manually on each route change, including the initial load.
		send_page_view: false,
	});

	const script = document.createElement('script');
	script.async = true;
	script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
	document.head.appendChild(script);

	initialized = true;
}

export function trackSignUp(method: string): void {
	gtagWindow().gtag?.('event', 'sign_up', { method });
}

// The dedupe key is the pathname only, not the pathname plus search. RootLayout keys its route
// effect on the pathname alone. So a query-param-only change must not produce a page_view. This
// module-level guard matches that behavior. It also absorbs React StrictMode double-effects and
// same-path re-runs.
let lastTrackedPagePath: string | undefined;

/**
 * Fire a GA4 page_view for the current route.
 * This is a no-op off web or when the tag is not attached.
 * It reports the exact URL (pathname plus search) but dedupes on pathname only.
 * So a query change on the same path does not emit a duplicate page_view.
 */
export function trackPageView(): void {
	const gtag = gtagWindow().gtag;
	if (typeof gtag !== 'function') {
		return;
	}

	const pathname = window.location.pathname;
	if (pathname === lastTrackedPagePath) {
		return;
	}
	lastTrackedPagePath = pathname;

	gtag('event', 'page_view', {
		page_location: window.location.href,
		page_path: pathname + window.location.search,
		page_title: document.title,
	});
}

// New registrations land on the free plan, the product's trial mode. So signup marks the trial
// start. A free signup has no billing cadence. The optional billingPeriod field exists only to
// prepare for a future paid trial. The event emits billing_period only when a cadence is provided.
export function trackStartTrial(params: { plan: string; billingPeriod?: 'monthly' | 'annual' }): void {
	gtagWindow().gtag?.('event', 'start_trial', {
		plan: params.plan,
		...(params.billingPeriod ? { billing_period: params.billingPeriod } : {}),
	});
}

// No trackPurchase here. Its only caller was the Stripe checkout return page, which had a session
// summary to report; a RevenueCat paywall hands back no price we could put in a `purchase` event.
// RevenueCat's own GA4 integration sends conversions from their side — a dashboard toggle, with the
// real charged amount, and it covers every store rather than just the web.

// --- AI assistant ---------------------------------------------------------------------------
//
// This section holds usage telemetry for the assistant. GA4 must receive no user content. It
// must never receive a prompt, a response, a merchant name, an amount, a filename, or a server
// error string. Each parameter below is a count, a boolean, a tool name from the fixed
// server-side tool list, or an allowlisted error code. The frontend already maps each error code
// to translated copy. Keep it that way. This is financial data, and an event parameter is the
// easiest place to leak it by accident.

/** Where the user opened the assistant. Both surfaces share one chat session. */
export type AssistantSurface = 'page' | 'drawer';

/** The assistant became visible to the user. This happens on a full page load or when the drawer opens. */
export function trackAssistantOpened(params: { surface: AssistantSurface }): void {
	gtagWindow().gtag?.('event', 'assistant_opened', { surface: params.surface });
}

/**
 * A turn was submitted.
 * `turn` is the 1-based index of this message within the conversation.
 * This lets the funnel separate first questions from follow-ups without carrying any text.
 */
export function trackAssistantMessageSent(params: { turn: number; hasAttachment: boolean }): void {
	gtagWindow().gtag?.('event', 'assistant_message_sent', {
		turn: params.turn,
		has_attachment: params.hasAttachment,
	});
}

/** A tool call started streaming. `tool` is the server's tool name. It never includes the arguments. */
export function trackAssistantToolCall(params: { tool: string }): void {
	gtagWindow().gtag?.('event', 'assistant_tool_call', { tool: params.tool });
}

/**
 * An error was shown to the user.
 * `code` is a stable identifier. It is either a coded server error the UI recognizes, or one of
 * the synthetic kinds the hook assigns (`http_<status>`, `stream`, `network`).
 * It is never the server's message text.
 */
export function trackAssistantError(params: { code: string }): void {
	gtagWindow().gtag?.('event', 'assistant_error', { code: params.code });
}

/**
 * A write tool ran. The server allows this only after the user confirms it.
 * `tool` identifies which write was confirmed.
 * The event reports nothing about what the write changed.
 */
export function trackAssistantWriteConfirmed(params: { tool: string }): void {
	gtagWindow().gtag?.('event', 'assistant_write_confirmed', { tool: params.tool });
}

/**
 * Resolve the GA4 client_id, the `_ga` cookie value.
 * Persist it at signup. The server-side purchase later uses it to attribute the sale to the same
 * visitor.
 * This resolves to undefined off web, when the tag is not loaded, or when gtag does not answer
 * within a short time budget (blocked or slow).
 * Resolve the GA4 client_id (the `_ga` cookie value) so it can be persisted at signup and later
 * used to attribute downstream events to the same visitor. Resolves to undefined off web, when the
 * tag isn't loaded, or if gtag doesn't answer within a short budget (blocked/slow).
 *
 * Note the server no longer sends anything with it — the Measurement Protocol call lived in the
 * Stripe webhook and went with it. It is still worth persisting: it is the join key between a
 * signup and whatever reports the eventual conversion, which is now RevenueCat's own integration.
 */
export function getGaClientId(): Promise<string | undefined> {
	const measurementId = getGaMeasurementId();
	const gtag = gtagWindow().gtag;

	if (!isWeb() || !measurementId || typeof gtag !== 'function') {
		return Promise.resolve(undefined);
	}

	return new Promise(resolve => {
		let settled = false;
		const done = (value?: string) => {
			if (!settled) {
				settled = true;
				resolve(value);
			}
		};

		try {
			gtag('get', measurementId, 'client_id', (clientId: string) => done(clientId));
		} catch {
			done(undefined);
		}

		// Never block signup on analytics. If gtag does not answer quickly, proceed without it.
		setTimeout(() => done(undefined), 1000);
	});
}
